/* global BigInt */
import { decodeFunctionData, stringToHex } from 'viem';
import {
  luckBlocksAddress,
  luckBlocksWAddress,
  ethLuckBlocksAddress,
  ethluckBlocksWAddress,
  maticLuckBlocksAddress,
  maticLuckBlocksWAddress,
  CRANNILuckBlocksAddress,
  CRANNILuckBlocksWAddress,
  bullvsbear,
  cdpAddress,
  cynthsOracleAddress,
  stakingContract,
  nftNodes,
  nftNodesCFG,
  governanceAddress,
  lbreferral,
  bestiariumAddress,
} from '../../.config';
import { bullsEyeAddress } from '../../pages/BullsEye/config';
import lbbtc from '../../abi/LuckBlocks.json';
import lbbtcw from '../../abi/lbbtcw.json';
import lbeth from '../../abi/ETHLuckBlocks.json';
import lbethw from '../../abi/lbethw.json';
import lbpol from '../../abi/MATICLuckBlocks.json';
import lbpolw from '../../abi/lbmaticw.json';
import lbcranni from '../../abi/CRANNILuckblocks.json';
import lbcranniw from '../../abi/lbcranniw.json';
import bullVsBearAbi from '../../abi/bullvsbearABI.json';
import bullsEyeAbi from '../../abi/BullsEye.json';
import cdpAbi from '../../abi/CynthsCDP.json';
import cynthsOracleAbi from '../../abi/CynthsOracle.json';
import stakingAbi from '../../abi/staking_abi.json';
import nodesAbi from '../../abi/luckBlocksNodes.json';
import nodesCfgAbi from '../../abi/luckBlocksNodesCFG.json';
import governanceAbi from '../../abi/LuckblocksGovernance.json';
import referralAbi from '../../abi/LBReferral.json';
import bestiariumAbi from '../../abi/Bestiarium.json';

/** Contracts a proposal can call: used to build proposals and to decode the ones already created */
export const GOVERNANCE_TARGETS = [
  { key: 'lotteryBtcDaily', label: 'BTC lottery · daily', address: luckBlocksAddress, abi: lbbtc.abi },
  { key: 'lotteryBtcWeekly', label: 'BTC lottery · weekly', address: luckBlocksWAddress, abi: lbbtcw.abi },
  { key: 'lotteryEthDaily', label: 'ETH lottery · daily', address: ethLuckBlocksAddress, abi: lbeth.abi },
  { key: 'lotteryEthWeekly', label: 'ETH lottery · weekly', address: ethluckBlocksWAddress, abi: lbethw.abi },
  { key: 'lotteryPolDaily', label: 'POL lottery · daily', address: maticLuckBlocksAddress, abi: lbpol.abi },
  { key: 'lotteryPolWeekly', label: 'POL lottery · weekly', address: maticLuckBlocksWAddress, abi: lbpolw.abi },
  { key: 'lotteryCranniDaily', label: 'CRANNI lottery · daily', address: CRANNILuckBlocksAddress, abi: lbcranni.abi },
  { key: 'lotteryCranniWeekly', label: 'CRANNI lottery · weekly', address: CRANNILuckBlocksWAddress, abi: lbcranniw.abi },
  { key: 'bullVsBear', label: 'Bull vs Bear', address: bullvsbear, abi: bullVsBearAbi },
  { key: 'bullsEye', label: 'BullsEye', address: bullsEyeAddress, abi: bullsEyeAbi.abi },
  { key: 'referral', label: 'Referrals', address: lbreferral, abi: referralAbi },
  { key: 'bestiarium', label: 'Cranni Bestiarium', address: bestiariumAddress, abi: bestiariumAbi.abi },
  { key: 'cdp', label: 'Cynths CDP', address: cdpAddress, abi: cdpAbi },
  { key: 'cynthsOracle', label: 'Cynths oracle', address: cynthsOracleAddress, abi: cynthsOracleAbi },
  { key: 'liquidityMining', label: 'Liquidity mining', address: stakingContract, abi: stakingAbi.abi },
  { key: 'nodesNft', label: 'NFT nodes (collection)', address: nftNodes, abi: nodesAbi.abi },
  { key: 'nodesConfig', label: 'NFT nodes (queue)', address: nftNodesCFG, abi: nodesCfgAbi.abi },
  { key: 'governance', label: 'Governance', address: governanceAddress, abi: governanceAbi },
].filter((target) => target.address);

export const findTarget = (address) =>
  GOVERNANCE_TARGETS.find((target) => target.address.toLowerCase() === (address || '').toLowerCase());

/** Functions that change state, alphabetical */
export const writableFunctions = (abi) =>
  abi
    .filter((item) => item.type === 'function' && ['nonpayable', 'payable'].includes(item.stateMutability))
    .sort((a, b) => a.name.localeCompare(b.name));

export const signatureOf = (fn) => `${fn.name}(${fn.inputs.map((input) => input.type).join(',')})`;

const toBigIntDeep = (value) => {
  if (Array.isArray(value)) return value.map(toBigIntDeep);
  if (typeof value === 'number') return BigInt(value);
  if (typeof value === 'string' && /^-?\d+$/.test(value)) return BigInt(value);
  return value;
};

/** Form text -> argument viem can encode (raw integer units, JSON for arrays and tuples) */
export const parseArgument = (type, raw) => {
  if (type.endsWith(']') || type.startsWith('tuple')) return toBigIntDeep(JSON.parse(raw));
  if (type === 'bool') return raw === true || raw === 'true';
  if (type.startsWith('uint') || type.startsWith('int')) {
    if (!/^-?\d+$/.test(String(raw).trim())) throw new Error(`${type}: integer in raw units expected`);
    return BigInt(String(raw).trim());
  }
  // tickers such as ciBTC can be typed as text
  if (type === 'bytes32' && !String(raw).startsWith('0x')) return stringToHex(String(raw), { size: 32 });
  return String(raw).trim();
};

const formatValue = (value) => {
  if (typeof value === 'bigint') return value.toString();
  if (Array.isArray(value)) return `[${value.map(formatValue).join(', ')}]`;
  if (value && typeof value === 'object') return `{${Object.values(value).map(formatValue).join(', ')}}`;
  return String(value);
};

/** What a proposal does, in words: "BullsEye.setTiers([0, 50, ...], [...])" */
export const describeCall = (targetAddress, data) => {
  const target = findTarget(targetAddress);
  if (target) {
    try {
      const { functionName, args = [] } = decodeFunctionData({ abi: target.abi, data });
      return { target, functionName, args: args.map(formatValue) };
    } catch (err) {
      // the target is known but the call is not in its ABI
    }
  }
  return { target, functionName: null, args: [], raw: data };
};
